import { SiteLayout } from "@/components/SiteLayout";
import { useSEO } from "@/hooks/useSEO";
import { useImageProtection } from "@/hooks/useImageProtection";
import { getCloudinaryUrl } from "@/lib/cloudinaryUtils";
import { ChevronLeft, ChevronRight, Undo2, Redo2, X, Download, Maximize2, Globe } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { 
  MEDIAKIT_CONFIG, 
  getMediaKitPages, 
  getPdfDownloadUrl, 
  isCloudinaryUrl,
  type MediaKitPage 
} from "@/config/mediakitConfig";
import {
  trackMediaKitViewPostHog,
  trackMediaKitPageViewPostHog,
  trackMediaKitNavigationPostHog,
  trackMediaKitFullscreenTogglePostHog,
  trackMediaKitDownloadClickedPostHog,
  trackMediaKitCompletedPostHog,
  trackMediaKitSessionEndPostHog,
  type MediaKitNavMethod,
  type MediaKitVariant,
} from "@/lib/posthogAnalytics";

export interface MediaKitIntroProps {
  /** Override pages when provided (e.g. for /mediakit variant) */
  pages?: MediaKitPage[];
  /** Start directly in fullscreen mode */
  startFullscreen?: boolean;
  /** Show download PDF button (default: from config) */
  enableDownload?: boolean;
  /** Show thumbnails (default: from config) */
  enableThumbnails?: boolean;
}

const STYLES = `
  .mediakit-page-container {
    perspective: 2000px;
    -webkit-perspective: 2000px;
    transform-style: preserve-3d;
    -webkit-transform-style: preserve-3d;
  }
  
  .mediakit-page {
    transform-style: preserve-3d;
    -webkit-transform-style: preserve-3d;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    box-shadow: 
      0 10px 40px rgba(0, 0, 0, 0.15),
      0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .mediakit-page-left {
    box-shadow: 
      -5px 10px 40px rgba(0, 0, 0, 0.15),
      -2px 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .mediakit-fullscreen {
    animation: mediakit-fade-in 0.3s ease-out;
    /* Safari/iOS: viewport height con fallbacks para address bar dinámico */
    min-height: 100vh;
    min-height: -webkit-fill-available;
    min-height: 100dvh;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
    -webkit-tap-highlight-color: transparent;
  }
  /* Contenedor del contenido: evita overflow en desktop y Safari, tamaño controlado */
  .mediakit-fullscreen-content {
    max-height: calc(100vh - 8rem);
    max-width: min(38rem, 80vw);
  }
  @supports (height: 100dvh) {
    .mediakit-fullscreen-content {
      max-height: calc(100dvh - 8rem);
    }
  }
  @media (max-width: 768px) {
    .mediakit-fullscreen-content {
      max-height: calc(100vh - 5rem);
      max-width: min(100%, 92vw);
    }
    @supports (height: 100dvh) {
      .mediakit-fullscreen-content {
        max-height: calc(100dvh - 6rem);
      }
    }
  }
  .mediakit-fullscreen-instant {
    animation: none;
  }
  
  @keyframes mediakit-fade-in {
    from {
      opacity: 0;
      background-color: rgba(0, 0, 0, 0);
    }
    to {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.95);
    }
  }
  
  @media (max-width: 768px) {
    .mediakit-page {
      box-shadow: 
        0 5px 20px rgba(0, 0, 0, 0.12),
        0 1px 5px rgba(0, 0, 0, 0.08);
    }
  }
`;

const MediaKitIntro = ({ pages: pagesOverride, startFullscreen = false, enableDownload, enableThumbnails }: MediaKitIntroProps = {}) => {
  useImageProtection();
  const { t } = useTranslation();
  const { country } = useTemplateSelection();
  
  const MEDIAKIT_PAGES = pagesOverride ?? getMediaKitPages();
  const MEDIAKIT_PDF_URL = getPdfDownloadUrl();
  const showDownload = enableDownload !== undefined ? enableDownload : Boolean(MEDIAKIT_CONFIG.features.enableDownload && MEDIAKIT_PDF_URL);
  const showThumbnails = enableThumbnails !== undefined ? enableThumbnails : MEDIAKIT_CONFIG.features.enableThumbnails;

  // Derive variant: "desktop" when startFullscreen (used by /mediakit), else "intro"
  const variant: MediaKitVariant = startFullscreen ? "desktop" : "intro";

  // Geo-block: MediaKit not available for Argentina (brands/agencies only)
  const isGeoBlocked = country === "AR";
  
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [isFullscreen, setIsFullscreen] = useState(startFullscreen);
  const [imageLoading, setImageLoading] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const dragConstraintsRef = useRef<HTMLDivElement>(null);

  // ── Analytics refs ─────────────────────────────────────────────────────────
  const sessionStartTime = useRef<number>(Date.now());
  const navMethodRef = useRef<MediaKitNavMethod>("button");
  const pagesVisited = useRef<Set<number>>(new Set([0]));
  const completedFired = useRef(false);
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;

  // Detección responsive reactiva: re-renderiza al cambiar viewport (rotación, resize)
  useEffect(() => {
    const checkViewport = () => setIsMobileViewport(window.innerWidth < 768);
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // ── MediaKit: view on mount + session end on unmount ──────────────────────
  useEffect(() => {
    trackMediaKitViewPostHog(variant, MEDIAKIT_PAGES.length, startFullscreen);
    // First page view (page 1)
    trackMediaKitPageViewPostHog(
      1,
      MEDIAKIT_PAGES[0]?.title ?? "Página 1",
      MEDIAKIT_PAGES.length,
      "button",
      isFullscreenRef.current,
      variant
    );

    return () => {
      const seconds = Math.round((Date.now() - sessionStartTime.current) / 1000);
      trackMediaKitSessionEndPostHog(
        MEDIAKIT_PAGES.length,
        pagesVisited.current.size,
        seconds,
        Math.max(...Array.from(pagesVisited.current)) + 1,
        variant
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── MediaKit: track each page change ─────────────────────────────────────
  useEffect(() => {
    if (currentPage === 0) return; // already tracked on mount
    pagesVisited.current.add(currentPage);
    trackMediaKitPageViewPostHog(
      currentPage + 1,
      MEDIAKIT_PAGES[currentPage]?.title ?? `Página ${currentPage + 1}`,
      MEDIAKIT_PAGES.length,
      navMethodRef.current,
      isFullscreenRef.current,
      variant
    );
    // Fire completed once when all pages visited
    if (!completedFired.current && pagesVisited.current.size === MEDIAKIT_PAGES.length) {
      completedFired.current = true;
      const seconds = Math.round((Date.now() - sessionStartTime.current) / 1000);
      trackMediaKitCompletedPostHog(MEDIAKIT_PAGES.length, seconds, variant);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Bloquear indexación en Google
  useEffect(() => {
    // Meta tags para bloquear indexación
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    const metaGooglebot = document.createElement('meta');
    metaGooglebot.name = 'googlebot';
    metaGooglebot.content = 'noindex, nofollow';
    document.head.appendChild(metaGooglebot);

    return () => {
      if (document.head.contains(metaRobots)) {
        document.head.removeChild(metaRobots);
      }
      if (document.head.contains(metaGooglebot)) {
        document.head.removeChild(metaGooglebot);
      }
    };
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = STYLES;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, []);

  // Bloquear scroll cuando está en fullscreen (Safari/iOS necesita position:fixed)
  useEffect(() => {
    if (isFullscreen) {
      const scrollY = window.scrollY;
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;
      const originalTouchAction = document.body.style.touchAction;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.touchAction = "pan-y pinch-zoom";

      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = originalWidth;
        document.body.style.touchAction = originalTouchAction;
        document.documentElement.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isFullscreen]);

  // Navegación con teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        trackMediaKitFullscreenTogglePostHog("close", currentPage + 1, variant);
        setIsFullscreen(false);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrevious("keyboard");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext("keyboard");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, currentPage]);

  const goToNext = useCallback((method: MediaKitNavMethod = "button") => {
    if (currentPage < MEDIAKIT_PAGES.length - 1) {
      navMethodRef.current = method;
      trackMediaKitNavigationPostHog(
        "next", method, currentPage + 1, currentPage + 2,
        MEDIAKIT_PAGES.length, isFullscreenRef.current, variant
      );
      setDirection("right");
      setImageLoading(true);
      setCurrentPage((prev) => prev + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const goToPrevious = useCallback((method: MediaKitNavMethod = "button") => {
    if (currentPage > 0) {
      navMethodRef.current = method;
      trackMediaKitNavigationPostHog(
        "prev", method, currentPage + 1, currentPage,
        MEDIAKIT_PAGES.length, isFullscreenRef.current, variant
      );
      setDirection("left");
      setImageLoading(true);
      setCurrentPage((prev) => prev - 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const goToPage = (index: number) => {
    if (index === currentPage) return;
    navMethodRef.current = "thumbnail";
    trackMediaKitNavigationPostHog(
      "jump", "thumbnail", currentPage + 1, index + 1,
      MEDIAKIT_PAGES.length, isFullscreenRef.current, variant
    );
    setDirection(index > currentPage ? "right" : "left");
    setImageLoading(true);
    setCurrentPage(index);
  };

  const getImageUrl = (image: string, imageMobile?: string, width?: number) => {
    // Seleccionar la imagen apropiada según viewport (responsive)
    const selectedImage = (isMobileViewport && imageMobile) ? imageMobile : image;
    
    // Si es una URL completa, retornarla directamente
    if (!isCloudinaryUrl(selectedImage)) {
      return selectedImage;
    }
    // Si es un public_id de Cloudinary
    // Construir URL manualmente con todas las transformaciones
    const cloudinaryUrl = `https://res.cloudinary.com/${import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your-cloud-name'}/image/upload`;
    const transformations: string[] = [];
    
    // Agregar width
    const targetWidth = width || MEDIAKIT_CONFIG.imageSettings.widths.large;
    transformations.push(`w_${targetWidth}`);
    
    // Agregar quality
    transformations.push(`q_${MEDIAKIT_CONFIG.imageSettings.quality}`);
    
    // Agregar formato automático
    transformations.push(`f_${MEDIAKIT_CONFIG.imageSettings.format}`);
    
    const transformString = transformations.join(',');
    
    // Si quieres usar watermark, usa getCloudinaryUrl
    if (MEDIAKIT_CONFIG.imageSettings.useWatermark) {
      return getCloudinaryUrl(`${cloudinaryUrl}/${transformString}/${selectedImage}`, {
        watermark: true,
      });
    }
    
    return `${cloudinaryUrl}/${transformString}/${selectedImage}`;
  };

  // Animaciones para el efecto de pasar página
  const pageVariants = {
    enter: (direction: "left" | "right") => ({
      rotateY: direction === "right" ? 90 : -90,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      rotateY: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: "left" | "right") => ({
      rotateY: direction === "right" ? -90 : 90,
      opacity: 0,
      scale: 0.8,
    }),
  };

  const pageTransition = MEDIAKIT_CONFIG.animations.pageTransition;

  // Handler para swipe/drag en móvil y desktop
  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = MEDIAKIT_CONFIG.animations.swipeThreshold;
    
    if (info.offset.x > swipeThreshold) {
      goToPrevious("swipe");
    } else if (info.offset.x < -swipeThreshold) {
      goToNext("swipe");
    }
  };

  const currentPageData = MEDIAKIT_PAGES[currentPage];
  const showMainContent = !isFullscreen;

  // ── Fullscreen-only render (used by /mediakit via startFullscreen=true) ──────
  // Skip SiteLayout entirely to avoid footer flash and useSiteSettings() loading.
  // The SiteLayout header/footer are irrelevant when always in fullscreen mode.
  if (startFullscreen && isFullscreen) {
    return (
      <div
        className="mediakit-fullscreen mediakit-fullscreen-instant fixed inset-0 z-[9999] bg-black pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
        style={{ background: "#000" }}
      >
        {/* Botón cerrar */}
        <button
          onClick={() => {
            trackMediaKitFullscreenTogglePostHog("close", currentPage + 1, variant);
            setIsFullscreen(false);
          }}
          className="absolute top-4 right-4 w-12 h-12 min-w-[48px] min-h-[48px] flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
          aria-label="Cerrar pantalla completa"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Navegación */}
        <button
          onClick={() => goToPrevious("button")}
          disabled={currentPage === 0}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-14 h-14 min-w-[56px] min-h-[56px] flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-20 disabled:cursor-not-allowed rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
          aria-label="Página anterior"
        >
          <Undo2 className="w-8 h-8" strokeWidth={2} />
        </button>

        <button
          onClick={() => goToNext("button")}
          disabled={currentPage === MEDIAKIT_PAGES.length - 1}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-14 h-14 min-w-[56px] min-h-[56px] flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-20 disabled:cursor-not-allowed rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
          aria-label="Página siguiente"
        >
          <Redo2 className="w-8 h-8" strokeWidth={2} />
        </button>

        {/* Imagen */}
        <div className="w-full h-full flex items-center justify-center px-3 py-4 md:px-6 md:py-6 overflow-hidden overscroll-none min-h-0">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentPage}
              custom={direction}
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={pageTransition}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              className="mediakit-fullscreen-content relative w-full cursor-grab active:cursor-grabbing touch-pan-y flex items-center justify-center"
              style={{ aspectRatio: MEDIAKIT_CONFIG.aspectRatio, WebkitTouchCallout: "none" }}
            >
              <img
                src={getImageUrl(currentPageData.image, currentPageData.imageMobile, isMobileViewport ? 800 : 1200)}
                alt={currentPageData.alt}
                className="w-full h-full max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // ── Geo-block UI for Argentina ───────────────────────────────────────────
  if (isGeoBlocked) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md text-center font-body">
            <Globe className="w-16 h-16 mx-auto mb-6 text-black/30" />
            <h1 className="text-2xl md:text-3xl font-display font-medium text-black mb-4">
              {t("mediaKit.geoBlockTitle")}
            </h1>
            <p className="text-black/60 mb-8 leading-relaxed">
              {t("mediaKit.geoBlockMessage")}
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-black text-white rounded-lg hover:bg-black/90 transition-colors"
            >
              {t("mediaKit.geoBlockCta")}
            </a>
          </div>
        </div>
      </SiteLayout>
    );
  }

  // ── Standard render with SiteLayout (used by /mediakit-intro) ────────────
  return (
    <SiteLayout>
      {showMainContent && (
      <div className="max-w-7xl mx-auto py-8 sm:py-12 md:py-16 px-4 font-body">
        {/* Header */}
        <header className="text-center mb-10 sm:mb-14">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-light text-black tracking-tight uppercase mb-3">
            {t("mediaKit.pageTitle")}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-black/20" />
            <p className="text-xs uppercase tracking-[0.2em] text-black/60 font-light">
              {t("mediaKit.pageSubtitle")}
            </p>
            <span className="w-12 h-px bg-black/20" />
          </div>
          <p className="text-sm text-black/70 max-w-2xl mx-auto leading-relaxed">
            {t("mediaKit.pageDescription")}
          </p>
        </header>

        {/* Visualizador de páginas */}
        <div className="mediakit-page-container max-w-5xl mx-auto mb-8" ref={dragConstraintsRef}>
          <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8 md:p-12">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentPage}
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={pageTransition}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                className="mediakit-page relative w-full bg-white rounded-lg overflow-hidden cursor-grab active:cursor-grabbing"
                style={{
                  aspectRatio: MEDIAKIT_CONFIG.aspectRatio,
                }}
              >
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                    <div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" />
                  </div>
                )}
                <img
                  src={getImageUrl(currentPageData.image, currentPageData.imageMobile, 1200)}
                  srcSet={`
                    ${getImageUrl(currentPageData.image, currentPageData.imageMobile, MEDIAKIT_CONFIG.imageSettings.widths.small)} ${MEDIAKIT_CONFIG.imageSettings.widths.small}w,
                    ${getImageUrl(currentPageData.image, currentPageData.imageMobile, MEDIAKIT_CONFIG.imageSettings.widths.medium)} ${MEDIAKIT_CONFIG.imageSettings.widths.medium}w,
                    ${getImageUrl(currentPageData.image, currentPageData.imageMobile, MEDIAKIT_CONFIG.imageSettings.widths.large)} ${MEDIAKIT_CONFIG.imageSettings.widths.large}w,
                    ${getImageUrl(currentPageData.image, currentPageData.imageMobile, MEDIAKIT_CONFIG.imageSettings.widths.xlarge)} ${MEDIAKIT_CONFIG.imageSettings.widths.xlarge}w
                  `}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                  alt={currentPageData.alt}
                  className="w-full h-full object-contain"
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
              </motion.div>
            </AnimatePresence>

            {/* Botones de navegación */}
            <button
              onClick={goToPrevious}
              disabled={currentPage === 0}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed rounded-full shadow-lg transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-black/20"
              aria-label={t("mediaKit.prevPage")}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={goToNext}
              disabled={currentPage === MEDIAKIT_PAGES.length - 1}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed rounded-full shadow-lg transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-black/20"
              aria-label={t("mediaKit.nextPage")}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Indicador de páginas */}
        {showThumbnails && (
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="text-sm text-black/60 font-light">
            {t("mediaKit.pageCounter", { current: currentPage + 1, total: MEDIAKIT_PAGES.length })}
          </span>
        </div>
        )}

        {/* Miniaturas */}
        {showThumbnails && (
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {MEDIAKIT_PAGES.map((page, index) => (
                <button
                  key={page.id}
                  onClick={() => goToPage(index)}
                  className={`relative rounded overflow-hidden transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-black/30 ${
                    currentPage === index
                      ? "ring-2 ring-black shadow-lg"
                      : "ring-1 ring-black/10 opacity-60 hover:opacity-100"
                  }`}
                  style={{
                    aspectRatio: MEDIAKIT_CONFIG.aspectRatio,
                  }}
                  aria-label={`Ir a página ${index + 1}`}
                >
                  <img
                    src={getImageUrl(page.image, page.imageMobile, MEDIAKIT_CONFIG.imageSettings.widths.thumbnail)}
                    alt={`Miniatura página ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-xs font-light">
                    {index + 1}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          {MEDIAKIT_CONFIG.features.enableFullscreen && !startFullscreen && (
            <button
              onClick={() => {
                trackMediaKitFullscreenTogglePostHog("open", currentPage + 1, variant);
                setIsFullscreen(true);
              }}
              className="px-6 py-3 bg-black text-white rounded-full hover:bg-black/90 transition-all flex items-center gap-2 text-sm uppercase tracking-wider font-light"
            >
              <Maximize2 className="w-4 h-4" />
              {t("mediaKit.fullscreenButtonText")}
            </button>
          )}
          
          {showDownload && MEDIAKIT_PDF_URL && (
            <a
              href={MEDIAKIT_PDF_URL}
              download="mediakit.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackMediaKitDownloadClickedPostHog(variant)}
              className="px-6 py-3 bg-white border-2 border-black text-black rounded-full hover:bg-black hover:text-white transition-all flex items-center gap-2 text-sm uppercase tracking-wider font-light"
            >
              <Download className="w-4 h-4" />
              {t("mediaKit.downloadButtonText")}
            </a>
          )}
        </div>

        {/* Link de regreso */}
        <div className="text-center">
          <a
            href="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-black/60 hover:text-black transition-colors font-light"
          >
            ← {t("mediaKit.backLinkText")}
          </a>
        </div>
      </div>
      )}

      {/* Vista en pantalla completa - se muestra al ingresar cuando startFullscreen */}
      {isFullscreen && (
        <div className={`mediakit-fullscreen font-body fixed inset-0 z-[9999] bg-black/95 ${startFullscreen ? 'mediakit-fullscreen-instant' : ''} pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]`}>
          {/* Botón cerrar - touch target 48px en mobile */}
          <button
            onClick={() => {
              trackMediaKitFullscreenTogglePostHog("close", currentPage + 1, variant);
              setIsFullscreen(false);
            }}
            className="absolute top-4 right-4 w-12 h-12 min-w-[48px] min-h-[48px] md:min-w-0 md:min-h-0 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
            aria-label={t("mediaKit.closeFull")}
          >
            <X className="w-6 h-6 md:w-6 md:h-6" />
          </button>

          {/* Contador de páginas - oculto en modo minimal (startFullscreen) */}
          {!startFullscreen && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm font-light tracking-wider z-30">
            {currentPage + 1} / {MEDIAKIT_PAGES.length}
          </div>
          )}

          {/* Navegación - touch targets más grandes en mobile */}
          <button
            onClick={goToPrevious}
            disabled={currentPage === 0}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-14 h-14 min-w-[56px] min-h-[56px] md:min-w-0 md:min-h-0 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-20 disabled:cursor-not-allowed rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
            aria-label={t("mediaKit.prevPage")}
          >
            <Undo2 className="w-8 h-8" strokeWidth={2} />
          </button>

          <button
            onClick={goToNext}
            disabled={currentPage === MEDIAKIT_PAGES.length - 1}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-14 h-14 min-w-[56px] min-h-[56px] md:min-w-0 md:min-h-0 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-20 disabled:cursor-not-allowed rounded-full z-30 transition-all focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation"
            aria-label={t("mediaKit.nextPage")}
          >
            <Redo2 className="w-8 h-8" strokeWidth={2} />
          </button>

          {/* Contenido - padding responsive, overflow hidden para Safari, tamaño controlado en desktop */}
          <div className="w-full h-full flex items-center justify-center px-3 py-4 md:px-6 md:py-6 overflow-hidden overscroll-none min-h-0">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentPage}
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={pageTransition}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                className="mediakit-fullscreen-content relative w-full cursor-grab active:cursor-grabbing touch-pan-y flex items-center justify-center"
                style={{
                  aspectRatio: MEDIAKIT_CONFIG.aspectRatio,
                  WebkitTouchCallout: "none",
                }}
              >
                <img
                  src={getImageUrl(currentPageData.image, currentPageData.imageMobile, isMobileViewport ? 800 : 1200)}
                  alt={currentPageData.alt}
                  className="w-full h-full max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                  draggable={false}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Hint para mobile - oculto en modo minimal (startFullscreen) */}
          {!startFullscreen && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs text-center px-4 md:hidden">
            {t("mediaKit.swipeHint")}
          </div>
          )}
        </div>
      )}
    </SiteLayout>
  );
};

export default MediaKitIntro;
